import React, { useEffect, useMemo, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, Grid, Typography } from "@mui/material"
import { useApiQueryTable } from "../../../../../../../core/api/components/ApiQueryTable"
import { MRT_ColumnDef, MRT_RowSelectionState } from "material-react-table"
import { CardSubscription, CreateOrEditPriceListFormValues } from "../../../priceListTypes"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { useFormikContext } from "formik"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { InfoBox } from "../../../../shops/components/formElements/InfoBox"
import { FormikSwitch } from "../../../../../../../shared/components/forms/Formik"
import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import {
    fetchIdCardsSubscriptionType,
    FetchIdCardsSubscriptionTypeResponse,
    PriceListQuery,
} from "../../../priceListApi"
import { useMerchant } from "../../../../../../merchants/merchantHooks"

const useStyles = makeStyles()((theme) => ({
    container: {
        gap: "2rem",
        flexDirection: "column",
    },
    tableTitle: {
        fontSize: "2rem",
        fontWeight: 500,
        color: theme.palette.tailwind.slate[600],
    },
    switchContainer: {
        width: "max-content",
    },
}))

export function transformData(data: FetchIdCardsSubscriptionTypeResponse) {
    return data.cardSubscriptions || []
}

function createInitialRowSelection(cards?: Omit<CardSubscription, "description">[]) {
    const initialSelection: {
        [key: string]: boolean
    } = {}

    cards?.forEach((card) => {
        initialSelection[card.id] = true
    })
    return initialSelection
}

export function FormPOSContent() {
    const { classes } = useStyles()
    const { merchantId } = useMerchant()

    const { l10n } = useAppLocalization()

    const { setFieldValue, values } = useFormikContext<CreateOrEditPriceListFormValues>()

    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>(() =>
        createInitialRowSelection(values.cardSubscriptions),
    )

    const queryResult = useApiQuery(fetchIdCardsSubscriptionType, {
        queryName: PriceListQuery.CARD_SUBSCRIPTIONS,
        dependencies: {
            merchantId,
        },
        select: transformData,
    })

    const addSelectedCardTypes = () => {
        if (!queryResult.data) return

        const selectedIds = Object.keys(rowSelection).filter((key) => rowSelection[key])

        const selectedCardTypes = queryResult.data.filter((card) => selectedIds.includes(card.id.toString())) || []

        setFieldValue("cardSubscriptions", selectedCardTypes)
    }

    useEffect(() => {
        addSelectedCardTypes()
    }, [rowSelection])

    const { ApiQueryTable, columnFactory } = useApiQueryTable(queryResult)

    const columns = useMemo<MRT_ColumnDef<CardSubscription>[]>(
        () => [
            columnFactory.shortNameColumn({
                header: l10n.getString("create-or-edit-price-list-pos-table-type"),
                accessorKey: "name",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("create-or-edit-price-list-pos-table-description"),
                accessorKey: "description",
            }),
        ],
        [l10n, columnFactory],
    )

    return (
        <Grid container className={classes.container}>
            <Box>
                <Box className={classes.switchContainer}>
                    <LocalizedStrict id="create-or-edit-price-list-pos-is-default-price-list">
                        <FormikSwitch<CreateOrEditPriceListFormValues> name="isDefaultPOSPriceList">
                            Default price list
                        </FormikSwitch>
                    </LocalizedStrict>
                </Box>
                <LocalizedStrict id="create-or-edit-price-list-pos-info-description" attrs={{ description: true }}>
                    <InfoBox description="One price list must be the default for the POS system. If an ID card is scanned and its subscription type is not assigned to a price list, the default price list will be shown." />
                </LocalizedStrict>
            </Box>
            <Grid item xs={12}>
                <ApiQueryTable
                    columns={columns}
                    initialState={{ density: "spacious" }}
                    enableRowSelection={true}
                    state={{
                        rowSelection,
                    }}
                    onRowSelectionChange={setRowSelection}
                    getRowId={(originalRow, index) => {
                        if (!originalRow.id) return index.toString()

                        return originalRow.id.toString()
                    }}
                    renderTopToolbar={() => (
                        <Box display="flex" alignItems="center" justifyContent="space-between" padding="1rem">
                            <LocalizedStrict id="create-or-edit-price-list-pos-table-title">
                                <Typography className={classes.tableTitle} variant="h6">
                                    ID card subscriptions
                                </Typography>
                            </LocalizedStrict>
                        </Box>
                    )}
                />
            </Grid>
        </Grid>
    )
}
